import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  ModalBody,
  Input,
  Label,
  Col,
  Row,
  FormFeedback,
  FormGroup,
  Spinner,
} from "reactstrap";
import { getCandidateExperiences } from "../../../../actions/customer";
import {
  createContent2,
  updateWorkExpierience,
  deleteWorkExperience, // 🟢 NEW import
} from "../../../../api/contents";
import { CustomSnackbar } from "../../../../helpers/CustomSnackbar";
import MonthOptions from "../../../../helpers/MonthOptions";
import YearOptions from "../../../../helpers/YearOptions";
import { useSearchParams } from "react-router-dom";
import ConfirmationModal from "../../../../helpers/Common/ConfirmationModal"; // 🟢 NEW import, same one used in QualificationModal

const ExperienceModal = ({
  openExperienceModal,
  setOpenExperienceModal,
  modalType = "add",
  experienceData,
}) => {
  const initialState = {
    position: "",
    company_name: "",
    start_year: "",
    start_month: "",
    end_year: "",
    end_month: "",
    details: "",
  };

  const getParsedFields = (fieldData) => {
    if (!fieldData) return {};
    if (typeof fieldData === "object") return fieldData;
    try {
      return JSON.parse(fieldData);
    } catch (e) {
      console.error("Failed to parse additionalFields:", e);
      return {};
    }
  };

  const [searchParams] = useSearchParams();
  const isInternal = searchParams.get("isInternal");

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentlyWorking, setCurrentlyWorking] = useState(false);

  // 🟢 NEW: delete-related state, same pattern as QualificationModal
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const triggerModal = () => setOpenExperienceModal(!openExperienceModal);

  const { profile } = useSelector((state) => state.customer);

  const dispatch = useDispatch();

  // 🟢 NEW: single source of truth for whether we're editing an existing experience
  const isEditingExisting = modalType === "update" || Boolean(experienceData?.id);

  const onChangeValue = (e, value) => {
    setFormData({ ...formData, [value]: e.target.value });
  };

  const onChangeCheckbox = (e) => {
    setCurrentlyWorking(e.target.checked);
    setFormData({ ...formData, end_year: "", end_month: "" });
  };

  useEffect(() => {
    const af = getParsedFields(
      experienceData?.additionalFields || experienceData?.additional_fields,
    );

    if (isEditingExisting && experienceData) {
      setFormData({
        position: experienceData?.position || af.position || "",
        company_name: experienceData?.company || af.company_name || "",
        start_year: experienceData?.startYear || af.start_year || "",
        start_month: experienceData?.startMonth || af.start_month || "",
        end_year: experienceData?.endYear || af.end_year || "",
        end_month: experienceData?.endMonth || af.end_month || "",
        details: experienceData?.details || af.details || "",
      });

      const workingStatus =
        experienceData?.isCurrentlyWorking !== undefined
          ? experienceData.isCurrentlyWorking
          : af.is_currently_working === "Yes" ||
            af.is_currently_working === true;

      setCurrentlyWorking(!!workingStatus);
    } else {
      setFormData(initialState);
      setCurrentlyWorking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceData, modalType]);

  const onSubmit = (e) => {
    e.preventDefault();

    setErrors(null);
    setLoading(true);

    const payload = {
      position: formData.position,
      company: formData.company_name,
      startMonth: formData.start_month,
      startYear: parseInt(formData.start_year) || 0,
      endMonth: currentlyWorking ? "" : formData.end_month,
      endYear: currentlyWorking ? 0 : parseInt(formData.end_year) || 0,
      isCurrentlyWorking: currentlyWorking,
      details: formData.details,
      type: "candidate_experiences",
      name: formData.position,
      displayOrder: 0,
      additionalFields: {
        ...formData,
        is_currently_working: currentlyWorking ? "Yes" : "No",
      },
    };

    const apiCall = !isEditingExisting
      ? createContent2(payload)
      : updateWorkExpierience(experienceData?.id, payload);

    apiCall
      .then((res) => {
        if (res?.status === 200 || res?.status === 201) {
          CustomSnackbar.success(
            `Your work experience ${
              !isEditingExisting ? "added" : "updated"
            } successfully.`,
          );

          triggerModal();
          setFormData(initialState);
          setCurrentlyWorking(false);

          dispatch(getCandidateExperiences());
        }
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong";
        CustomSnackbar.error(errorMsg);
      })
      .finally(() => setLoading(false));
  };

  // 🟢 NEW: delete handler, same pattern as QualificationModal's handleDelete
  const handleDelete = () => {
    if (!experienceData?.id) return;

    setDeleteLoading(true);

    deleteWorkExperience(experienceData.id)
      .then((res) => {
        if (res?.status === 200 || res?.status === 201 || res?.status === 204) {
          triggerModal();
          CustomSnackbar.success("Work experience deleted successfully.");
          dispatch(getCandidateExperiences());
        }
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong";
        CustomSnackbar.error(errorMsg);
      })
      .finally(() => {
        setDeleteLoading(false);
        setOpenDeleteModal(false);
      });
  };

  return (
    <>
      <div
        className="modal fade"
        id="applyNow"
        tabIndex="-1"
        aria-labelledby="applyNow"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <Modal isOpen={openExperienceModal} toggle={triggerModal} centered>
            <ModalBody className="modal-body p-5">
              <div className="text-center mb-4">
                <h5 className="modal-title" id="staticBackdropLabel">
                  {isEditingExisting ? "Update" : "Add"} Work Experience
                </h5>
              </div>
              <div className="position-absolute end-0 top-0 p-3">
                <button
                  type="button"
                  onClick={triggerModal}
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>

              <form onSubmit={onSubmit}>
                <div className="mb-3">
                  <Label for="positionControlInput" className="form-label">
                    Position *
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    id="positionControlInput"
                    placeholder="Enter your position or title"
                    value={formData.position}
                    onChange={(e) => onChangeValue(e, "position")}
                    invalid={errors?.position ? true : false}
                  />
                  <FormFeedback>{errors?.position}</FormFeedback>
                </div>

                <div className="mb-3">
                  <Label for="companyControlInput2" className="form-label">
                    Company *
                  </Label>
                  <Input
                    type="text"
                    className="form-control"
                    id="companyControlInput2"
                    placeholder="Enter company name"
                    value={formData.company_name}
                    onChange={(e) => onChangeValue(e, "company_name")}
                    invalid={errors?.company_name ? true : false}
                  />
                  <FormFeedback>{errors?.company_name}</FormFeedback>
                </div>

                <div className="mb-3">
                  <Label for="select" className="form-label">
                    Start *
                  </Label>
                  <Row>
                    <Col md={6}>
                      <Input
                        type="select"
                        className="form-select"
                        name="choices-start-month"
                        id="choices-start-month"
                        value={formData?.start_month}
                        onChange={(e) => onChangeValue(e, "start_month")}
                        invalid={errors?.start_month ? true : false}
                      >
                        <MonthOptions />
                      </Input>
                      <FormFeedback>{errors?.start_month}</FormFeedback>
                    </Col>

                    <Col md={6}>
                      <Input
                        type="select"
                        className="form-select"
                        name="choices-start-year"
                        id="choices-start-year"
                        value={formData?.start_year}
                        onChange={(e) => onChangeValue(e, "start_year")}
                        invalid={errors?.start_year ? true : false}
                      >
                        <YearOptions />
                      </Input>
                      <FormFeedback>{errors?.start_year}</FormFeedback>
                    </Col>
                  </Row>
                </div>

                <div className="mb-3">
                  <Label for="select" className="form-label">
                    End *
                  </Label>
                  <Row>
                    <Col md={6}>
                      <Input
                        type="select"
                        className="form-select"
                        name="choices-end-month"
                        id="choices-end-month"
                        value={formData?.end_month}
                        onChange={(e) => onChangeValue(e, "end_month")}
                        invalid={errors?.end_month ? true : false}
                        disabled={currentlyWorking}
                      >
                        <MonthOptions />
                      </Input>
                      <FormFeedback>{errors?.end_month}</FormFeedback>
                    </Col>

                    <Col md={6}>
                      <Input
                        type="select"
                        className="form-select"
                        name="choices-end-year"
                        id="choices-end-year"
                        value={formData?.end_year}
                        onChange={(e) => onChangeValue(e, "end_year")}
                        invalid={errors?.end_year ? true : false}
                        disabled={currentlyWorking}
                      >
                        <YearOptions />
                      </Input>
                      <FormFeedback>{errors?.end_year}</FormFeedback>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={12}>
                      <FormGroup check inline>
                        <Input
                          type="checkbox"
                          id="work-checkbox"
                          onChange={onChangeCheckbox}
                          checked={currentlyWorking}
                        />
                        <Label check htmlFor="work-checkbox">
                          I'm currently working here
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                </div>

                <div className="mb-3">
                  <Label for="messageControlTextarea" className="form-label">
                    Details
                  </Label>
                  <Input
                    className="form-control"
                    id="messageControlTextarea"
                    rows="4"
                    placeholder="Describe your work experience"
                    value={formData.details}
                    onChange={(e) => onChangeValue(e, "details")}
                    type="textarea"
                    invalid={errors?.details ? true : false}
                  />
                  <FormFeedback>{errors?.details}</FormFeedback>
                </div>

                {/* 🟢 CHANGED: layout updated to include Delete button on the left, same as QualificationModal */}
                <div className="d-flex justify-content-between align-items-center">
                  {isEditingExisting ? (
                    <button
                      type="button"
                      onClick={() => setOpenDeleteModal(true)}
                      className="btn btn-danger"
                    >
                      Delete
                    </button>
                  ) : (
                    <span />
                  )}

                  <div className="text-end">
                    <button
                      type="button"
                      onClick={triggerModal}
                      className="btn btn-secondary me-2"
                    >
                      Cancel
                    </button>
                    {loading ? (
                      <button
                        type="button"
                        className="btn btn-primary disabled"
                      >
                        Save <Spinner size="sm" />
                      </button>
                    ) : (
                      <button type="submit" className="btn btn-primary">
                        Save
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </ModalBody>
          </Modal>
        </div>
      </div>

      {/* 🟢 NEW: confirmation modal, same component used in QualificationModal */}
      <ConfirmationModal
        title="Delete work experience"
        message="Are you sure you want to delete this work experience ?"
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        onSubmit={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

export default ExperienceModal;